import { Component, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonMaterialModule } from '@nrwl/common-material';
import { CommonModule } from '@angular/common';
import { TemplateService } from '@nrwl/test-setup';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import {
  CommonLibModule,
  DialogService,
  UserAuth,
  UserService,
  NotificationService,
  FUSION_TEMPLATE_ACTION_NAME,
  TestTypeValue,
  TimeoutWrapperService
} from '@nrwl/common-lib';
import { ViaviTableComponent } from '@nrwl/viavi-tabulator';
import { TemplateDataComponent } from './template-data/template-data.component';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NavigationProvider } from '@nrwl/navigation-abstract';

@Component({
  selector: 'viavi-template-management',
  standalone: true,
  imports: [CommonModule, CommonMaterialModule, CommonLibModule, TemplateDataComponent],
  templateUrl: './template-tabs.component.html',
  styleUrls: ['./template-tabs.component.scss']
})
export class TemplateTabsComponent implements OnInit, OnDestroy {
  @ViewChild(ViaviTableComponent) child: ViaviTableComponent;
  // eslint-disable-next-line
  hasReadWriteRole: boolean;
  hasManagePermissionRole: boolean;
  isLoading: boolean;
  // eslint-disable-next-line
  templatesResponse: any;
  // eslint-disable-next-line
  templateDataSource: any;
  selectedIndex = null;
  currentUser: string;
  myTemplateColumns = ['name', 'type', 'accessLevel', 'lastModified', 'actions'];
  sharedTemplateColumns = ['name', 'type', 'owner', 'accessLevel', 'lastModified', 'actions'];
  // eslint-disable-next-line
  dialogData: any;
  isCheckBoxEnabled = true;
  title = 'Test templates';
  tabIndex: number;
  resetCheckbox: Subject<string[]>;
  private readonly unsubscribe: Subject<void> = new Subject();
  filterType: any;

  constructor(
    private readonly userAuth: UserAuth,
    private readonly userService: UserService,
    private readonly templateService: TemplateService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
    private readonly timeout: TimeoutWrapperService,
    private navigator: NavigationProvider,
    private injector: Injector
  ) {
    this.dialogData = this.injector.get(MAT_DIALOG_DATA, null);
    this.resetCheckbox = new Subject<string[]>();
  }

  ngOnInit(): void {
    this.isLoading = true;
    if (this.dialogData) {
      this.filterType = this.dialogData?.filterType;
      this.isCheckBoxEnabled = false;
      this.title = 'Open template';
      this.myTemplateColumns = this.dialogData?.myTemplatesColumnHeaders;
      this.sharedTemplateColumns = this.dialogData?.sharedTemplatesColumnHeaders;
    }
    this.currentUser = this.userService.getUserName();
    this.hasReadWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
    this.hasManagePermissionRole = this.userAuth.isAuthedFor('MANAGE_PERMISSIONS');
    this.getTemplates(0);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
  // eslint-disable-next-line
  getTemplates(index: number): any {
    const payload = {
      returnOnly: 'all',
      listOnly: true
    };
    this.templateService
      ._findTestTemplates(payload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.isLoading = false;
          this.templatesResponse = res.data;
          this.getTabDataBasedOnIndex(index);
          this.filterTemplatesWithTestType();
        },
        err => {
          this.isLoading = false;
        }
      );
  }
  filterTemplatesWithTestType(): void {
    if (this.dialogData?.filterType.length > 0) {
      this.templateDataSource = this.templateDataSource.filter(
        item =>
          this.dialogData?.filterType.toString().toLocaleLowerCase().indexOf(item.testType) !== -1
      );
    }
  }
  // eslint-disable-next-line
  onSelectTab(event): void {
    this.getTabDataBasedOnIndex(event.index);
    this.filterTemplatesWithTestType();
  }
  refreshTemplates(): void {
    if (this.tabIndex === 0) {
      this.getTemplates(this.tabIndex);
    } else if (this.tabIndex === 1) {
      this.getTemplates(this.tabIndex);
    } else {
      this.getTemplates(this.tabIndex);
    }
  }

  getTabDataBasedOnIndex(index: number): void {
    this.tabIndex = index;
    if (index === 0) {
      this.templateDataSource = this.templatesResponse.filter(
        item => item.user === this.currentUser
      );
    } else if (index === 1) {
      this.templateDataSource = this.templatesResponse.filter(
        item => item.accessLevel === 'shared'
      );
    } else {
      this.templateDataSource = this.templatesResponse;
    }
    this.selectedIndex = index;
  }

  // eslint-disable-next-line
  openTemplates(action): any {
    const defaultParams = {
      modeType: 'template',
      templateId: action.id
    };
    let pageId = action.testType + '_test';
    switch (action.testType) {
      case TestTypeValue.SERVICE_CONTINUITY:
        pageId = 'service_test';
        break;
      case TestTypeValue.MULTISTEP:
        pageId = 'multistep_test';
        break;
      case TestTypeValue.TWAMP:
        pageId = 'twamp_test';
        break;
      case TestTypeValue.TWAMP_LIGHT_REFLECTOR:
      case TestTypeValue.TWAMP_REFLECTOR:
        pageId = 'twamp_reflector_test';
        break;
    }
    this.navigator.navigateToTestSetup(pageId, {
      queryParams: {
        defaultParams: JSON.stringify(defaultParams)
      }
    });
  }
  // eslint-disable-next-line
  setAsDefaults(action): any {
    this.isLoading = true;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.templateService
      ._setDefaultTemplate(action.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.isLoading = false;
          this.getTemplates(this.selectedIndex);
          const successMsg = 'Template has been updated.';
          this.notificationService.onSuccess(successMsg);
        },
        err => {
          this.isLoading = false;
          this.notificationService.onFail(err.error.data.result);
        }
      );
  }
  // eslint-disable-next-line
  unsetAsDefaults(action): any {
    this.isLoading = true;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.templateService
      ._unsetDefaultTemplate(action.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.getTemplates(this.selectedIndex);
          this.isLoading = false;
          const successMsg = 'Template has been updated.';
          this.notificationService.onSuccess(successMsg);
        },
        err => {
          this.isLoading = false;
          this.notificationService.onFail(err.error.data.result);
        }
      );
  }
  // eslint-disable-next-line
  shareTemplates(action): any {
    this.isLoading = true;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.templateService
      ._shareTemplate(action.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.getTemplates(this.selectedIndex);
          this.isLoading = false;
          this.notificationService.onSuccess(res.data.message);
        },
        err => {
          this.isLoading = false;
          this.notificationService.onFail(err.error.data.message);
        }
      );
  }
  // eslint-disable-next-line
  unShareTemplates(action): any {
    this.isLoading = true;
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.templateService
      ._unshareTemplate(action.id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          this.getTemplates(this.selectedIndex);
          this.isLoading = false;
          this.notificationService.onSuccess(res.data.message);
        },
        err => {
          this.isLoading = false;
          this.notificationService.onFail(err.error.data.message);
        }
      );
  }
  // eslint-disable-next-line
  removeTemplates(selectedItems, actionName): any {
    let param;
    if (actionName === 'bulkRemove') {
      param = {
        id: 'datasource.removeTemplates',
        callBackService: this,
        callBackFuntion: this.callBackRemove,
        callBackFnParams: {
          selectedItems: selectedItems,
          self: this
        }
      };
    } else {
      selectedItems = [selectedItems.id];
      param = {
        id: 'datasource.removeTemplate',
        callBackService: this,
        callBackFuntion: this.callBackRemove,
        callBackFnParams: {
          selectedItems: selectedItems,
          self: this
        }
      };
    }

    this.dialogService.showDialog('confirm', 'body', param);
  }
  //eslint-disable-next-line
  callBackRemove(service, params): any {
    service.isLoading = true;
    service.templateService._removeTemplate(params.selectedItems).subscribe(
      res => {
        service.notificationService.onSuccess(res.data.message);
        service.resetCheckbox.next(params.selectedItems);
        service.removeTemplatesFromDataSource(params.selectedItems);
        service.timeout.add(() => {
          service.getTemplates(service.selectedIndex);
        }, 6000);
        service.isLoading = false;
      },
      err => {
        service.isLoading = false;
        service.notificationService.onFail(err.error.data.message);
      }
    );
  }

  removeTemplatesFromDataSource(templateIds: string[]): void {
    const removeSet = new Set(templateIds);
    this.templateDataSource = this.templateDataSource.filter(
      template => !removeSet.has(template.id)
    );
  }

  // eslint-disable-next-line
  onRowActionClick(action: any): void {
    switch (action.label) {
      case FUSION_TEMPLATE_ACTION_NAME.OPEN:
        this.openTemplates(action.rowData);
        break;

      case FUSION_TEMPLATE_ACTION_NAME.SET_AS_DEFAULTS:
        this.setAsDefaults(action.rowData);
        break;
      case FUSION_TEMPLATE_ACTION_NAME.UNSET_AS_DEFAULTS:
        this.unsetAsDefaults(action.rowData);
        break;
      case FUSION_TEMPLATE_ACTION_NAME.SHARE:
        this.shareTemplates(action.rowData);
        break;
      case FUSION_TEMPLATE_ACTION_NAME.UNSHARE:
        this.unShareTemplates(action.rowData);
        break;
      case FUSION_TEMPLATE_ACTION_NAME.REMOVE:
        this.removeTemplates(action.rowData, 'remove');
        break;
    }
  }
}
